"use strict";

import {
    Alert,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { getProjectsForUser, getUserById, updateUser } from "../features/users/api";
import { getStoredUserInfo } from "../utils/storage";

const UserEditScreen = () => {
    // basic info about the user being edited
    const params = useParams();
    const { id: userId } = params;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [admin, setAdmin] = useState(false);

    // track projects and save status
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    // fetch projects assigned to this user
    const fetchProjects = useCallback(async () => {
        setProjectsLoading(true);
        setProjectsError(null);

        try {
            // retrieve current user's token for authorization
            const stored = getStoredUserInfo();
            if (!stored) {
                setProjectsLoading(false);
                return;
            }
            const { token } = stored;

            // request project assignments for the edited user
            const response = await getProjectsForUser(
                { userId },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const data = response.data;

            // update state with fetched projects
            setProjects(
                (data.projects || []).map((p) => ({
                    ...p,
                    roles: Array.isArray(p.roles)
                        ? p.roles
                        : Array.isArray(p.role)
                        ? p.role
                        : (p.role ? [p.role] : []),
                }))
            );
            setProjectsLoading(false);
        } catch (error) {
            setProjectsError({ status: error.response?.status ?? "unknown" });
            setProjects([]);
            console.error("Failed to fetch projects:", error);
            setProjectsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        // load user details and assigned projects on mount
        const fetchData = async () => {
            try {
                const response = await getUserById(userId);
                setEmail(response.data.email);
                setName(response.data.name);
                setAdmin(response.data.isAdmin);
                await fetchProjects();
            } catch (err) {
                console.error("Failed to load user:", err);
                setError(`Couldn't load this user: ${err.normalized?.message ?? err.message}`);
            }
        };
        fetchData();
    }, [fetchProjects, userId]);

    // send updated user data to the server
    const updatedUser = async (e) => {
        e.preventDefault();
        setSaved(false);
        setError(null);

        try {
            const response = await updateUser({
                id: userId,
                name: name,
                isAdmin: admin
            });
            console.log(response.data.msg);
            setSaved(true);
        } catch (error) {
            console.error("Failed to save user:", error);
            setError(`Couldn't save changes: ${error.normalized?.message ?? error.message}`);
        }
    };

    return (
        <>
            {saved && (
                <Alert severity="success" sx={{ width: "100%", borderRadius: 0 }}>
                    Saved
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ width: "100%", borderRadius: 0 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            <Grid item container justifyContent="center">
                <Grid item container xs={7} marginTop={7} direction="column">
                    <Grid item mb={2}>
                        <Breadcrumbs>
                            <Link component={RouterLink} to="/admin/users" underline="hover" color="inherit">
                                Users
                            </Link>
                            <Typography color="text.primary">Edit User</Typography>
                        </Breadcrumbs>
                    </Grid>
                    <form onSubmit={updatedUser}>
                        <Grid item container direction="column">
                            <Grid item>
                                <Typography variant="h6">ID: {userId}</Typography>
                            </Grid>

                            <Grid item marginTop={1.5} marginBottom={1.5}>
                                <Typography>Name</Typography>
                            </Grid>
                            <TextField
                                type="text"
                                placeholder="Enter your Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Grid item marginBottom={1.5} marginTop={1.5}>
                                <Typography>Email</Typography>
                            </Grid>
                            <TextField
                                type="text"
                                placeholder="Enter your email"
                                value={email}
                                disabled
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <Grid item marginBottom={1.5} marginTop={1.5}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={admin}
                                            onChange={(e) => setAdmin(e.target.checked)}
                                        />
                                    }
                                    label="Admin"
                                />
                            </Grid>

                            {/* display the user's project roles */}
                            <Grid item marginTop={2}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Project</TableCell>
                                            <TableCell>Roles</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {projectsLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Loading projects...
                                                </TableCell>
                                            </TableRow>
                                        ) : projectsError ? (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    Unable to load projects. Status:{" "}
                                                    {projectsError.status}
                                                </TableCell>
                                            </TableRow>
                                        ) : projects && projects.length > 0 ? (
                                            projects.map((project) => (
                                                <TableRow key={project.id || project.name}>
                                                    <TableCell>{project.name}</TableCell>
                                                    <TableCell>
                                                        {(project.roles || []).join(", ")}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    No projects assigned
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            container
                            direction="column"
                            alignItems="center"
                            marginTop={3.5}
                            marginBottom={4}
                        >
                            <Button variant="contained" type="submit">
                                Save
                            </Button>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default UserEditScreen;
